import React, { useState, useEffect, useContext, useMemo, useRef } from 'react'
import axios from 'axios'
import { StateContext } from '../../AppContext'
import { IState, ParamTypes } from 'src/Interface'
import { useLocation, useParams } from 'react-router-dom'
import { baseApiURL } from 'src/utils/utils'
import Header from 'src/components/header/Header'
import Button from 'src/components/button/Button'
import { usePagination, useSortBy, useTable } from 'react-table'
import SearchField from 'src/components/searchField/SearchField'
import { toast } from 'react-toastify'
import Footer from 'src/components/footer/Footer'
import Table from 'src/components/table/Table'
import SubHeader from 'src/components/subHeader/SubHeader'
import { DispatchContext } from 'src/AppContext'
import { useQuery } from '@tanstack/react-query'
import useDebounce from 'src/hooks/useDebounce'

const Users: React.FunctionComponent = () => {
  const state: IState = useContext(StateContext)
  const { companyId } = useParams<ParamTypes>()
  const dispatch: React.Dispatch<any> = useContext(DispatchContext)
  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const debouncedSearchValue = useDebounce(searchValue, 300)
  const hasData = useRef(false)
  const prevSearchRef = useRef(debouncedSearchValue)
  const pageSize = 10
  const location: any = useLocation()

  const columns = useMemo(() => [
    {
      Header: 'Label navn',
      accessor: 'description'
    },
    {
      Header: 'Egenskabs kode',
      accessor: 'code'
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: (cell) => cell.value === 'ACTIVE' ? <span className='status status--green'>Aktiv</span> : <span className='status status--red'>Inaktiv</span>
    }
  ], [])

  const [attributes, setAttributes] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const data = useMemo(() => attributes, [attributes])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canNextPage,
    canPreviousPage,
    pageOptions,
    previousPage,
    gotoPage,
    nextPage,
    prepareRow,
    state: pageState,
  } = useTable({
    columns,
    data,
    autoResetPage: false,
    autoResetSortBy: false,
    manualPagination: true,
    pageCount: totalCount > 0 ? Math.ceil(totalCount / pageSize) : 0
  }, useSortBy, usePagination)

  // Reset to first page when search changes
  useEffect(() => {
    const searchChanged = prevSearchRef.current !== debouncedSearchValue
    
    if (searchChanged && pageState.pageIndex > 0) {
      gotoPage(0)
    }
    
    prevSearchRef.current = debouncedSearchValue
  }, [debouncedSearchValue, gotoPage, pageState.pageIndex])

  // Convert 0-based pageIndex to 1-based page number for API
  const currentPage = (pageState.pageIndex || 0) + 1
  const currentSort = pageState.sortBy?.[0]

  const { data: queryData, isLoading: isQueryLoading, isFetching, error } = useQuery({
    queryKey: ['attributes', currentPage, currentSort?.id, currentSort?.desc, debouncedSearchValue],
    queryFn: async () => {
      // Skip API call if search value is 1-2 characters (wait for at least 3 characters)
      if (debouncedSearchValue && debouncedSearchValue.length > 0 && debouncedSearchValue.length < 3) {
        return
      }

      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: pageSize.toString()
      })
      if (currentSort?.id) {
        queryParams.append('sortBy', currentSort.id)
        queryParams.append('sortOrder', currentSort.desc ? 'DESC' : 'ASC')
      }
      if (debouncedSearchValue && debouncedSearchValue.length >= 3) {
        queryParams.append('searchText', debouncedSearchValue)
      }

      const result = await axios(`${baseApiURL()}/attributes?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${state.user.accessToken}`
        }
      })
      const responseData = result.data.records || result.data
      const attributes = Array.isArray(responseData) ? responseData : []
      const totalRecords = result.data.totalRecords || attributes.length
      return { attributes, totalRecords }
    },
    enabled: !!state.user.accessToken && (!debouncedSearchValue || debouncedSearchValue.length === 0 || debouncedSearchValue.length >= 3),
    onError: (err: any) => {
      toast(err.response?.data?.message || 'Fejl ved indlæsning af attributter')
    }
  })

  // Manage loading states based on whether we have data
  useEffect(() => {
    if (isFetching) {
      if (hasData.current) {
        setTableLoading(true)
        setLoading(false)
      } else {
        setLoading(true)
        setTableLoading(false)
      }
    } else {
      setLoading(false)
      setTableLoading(false)
      if (queryData) {
        hasData.current = true
      }
    }
  }, [isFetching, queryData])

  // Update local state and global state when query data changes
  useEffect(() => {
    if (queryData) {
      setAttributes(queryData.attributes || [])
      setTotalCount(queryData.totalRecords || 0)
      dispatch({ type: 'SET_ATTRIBUTES', attributes: queryData.attributes || [] })
    }
  }, [queryData, dispatch])

  return (
    <>
      <Header />
      <SubHeader loading={false} backPath={location.state.backPath ? location.state.backPath : '/companies'}
        title={
          <>
            <svg className='arrow-icon' xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg> <span className='color-gray tw-font-normal'>{location.state.from}</span> <span className='tw-mx-3 color-gray tw-font-normal'>/</span> <span className='text-black text-bold'>Attributter</span>
          </>
        }>
        <SearchField name='search' label='Søg' placeholder='Søg' handleInputChange={(value) => setSearchValue(value)} />
        <Button modifiers='tw-ml-5' type='primary' label='Opret attribut' handleClick={() => dispatch({ showModal: true, type: 'SHOW_MODAL', modalType: 'addAttribute' })} />
      </SubHeader>
      <main className='container'>
        <Table
          getTableBodyProps={getTableBodyProps}
          getTableProps={getTableProps}
          loading={tableLoading}
          prepareRow={prepareRow}
          page={page}
          headerGroups={headerGroups}
          handleClick={(attributeId) => dispatch({ type: 'SHOW_ASIDE', show: true, asideType: 'AttributeDetails', attributeId, size: 'small' })}
          type='attributes'
          companyId={companyId}
          state={{ background: location }}
        />
      </main>
      <Footer
        pageState={pageState}
        canNextPage={canNextPage}
        nextPage={nextPage}
        canPreviousPage={canPreviousPage}
        loading={false}
        page={page}
        paginationLabel='egenskaber'
        gotoPage={gotoPage}
        previousPage={previousPage}
        pageOptions={pageOptions}
        itemsLength={totalCount}
      />
    </>
  )
}

export default Users
